import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Check, Copy, ExternalLink, Globe2, Rocket, Share2, Store, X } from 'lucide-react';

import { getSearchCollection } from '@services/stockSearch';
import { useShop } from '@/hooks/useShop';
import { useInventoryPageSummary } from '@/hooks/useInventory';
import { useCountUp } from '@/hooks/useCountUp';
import { copyToClipboard } from '@/lib/utils';

export const STOREFRONT_LAUNCHED_KEY = 'unify:storefront-launched';

/**
 * Persist that the storefront has been launched and notify same-tab listeners
 * (LaunchHighlight listens for the event — localStorage `storage` events only
 * fire across tabs). Every `onActivated` call site pairs this with the actual
 * channel enable.
 */
export const markStorefrontLaunched = () => {
	if (typeof window === 'undefined') return;
	window.localStorage.setItem(STOREFRONT_LAUNCHED_KEY, '1');
	window.dispatchEvent(new Event('storefront:launched'));
};

// How long the "searchable products" bar takes to fill in the counter phase.
const BAR_FILL_MS = 2400;

// Per-step dwell times for the anticipation phase — the storefront isn't
// really being assembled step by step, but the staged reveal sets up the
// celebration that follows.
const ANTICIPATION_STEPS = [
	{ label: 'Setting up Local Storefront framework', ms: 600 },
	{ label: 'Adding store details', ms: 500 },
	{ label: 'Ingesting in-store inventory data', ms: 800 },
	{ label: 'Barcodes and prices received', ms: 500 },
	{ label: 'Finding public product profiles', ms: 800 },
	{ label: 'Matches found', ms: 500 },
	{ label: 'Adding products to page', ms: 700 },
	{ label: 'Optimising for search engines', ms: 700 },
	{ label: 'Optimising for AI chat… (this can take a few seconds)', ms: 1500 },
	{ label: 'Running tests…', ms: 700 },
	{ label: 'All tests positive', ms: 400 },
	{ label: 'Ready to go live', ms: 300 }
];

const slugify = (name) => (name ?? 'your-store').toLowerCase().trim().replace(/\s+/g, '-');

const CONFETTI_PALETTE = ['hsl(var(--primary))', 'hsl(var(--action))', 'hsl(var(--success))', '#EC4899', '#F97316', '#8B5CF6'];
const CONFETTI_SHAPES = ['rect', 'circle', 'strip'];

/** Whether the user has asked the OS to minimise non-essential motion. */
const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Big celebratory confetti — two cannon bursts plus a steady fall and a radial flash. */
function Confetti() {
	const { fall, burstL, burstR } = useMemo(() => {
		const fallPieces = Array.from({ length: 90 }).map((_, i) => ({
			i,
			left: Math.random() * 100,
			delay: Math.random() * 1.4,
			duration: 2.4 + Math.random() * 2.6,
			rotate: Math.random() * 360,
			drift: (Math.random() - 0.5) * 80,
			color: CONFETTI_PALETTE[i % CONFETTI_PALETTE.length],
			shape: CONFETTI_SHAPES[i % CONFETTI_SHAPES.length],
			size: 6 + Math.random() * 8
		}));
		const makeBurst = (side, n = 55) =>
			Array.from({ length: n }).map((_, i) => {
				const angle = -Math.PI / 2 + (Math.random() - 0.5) * (Math.PI * 0.75);
				const power = 240 + Math.random() * 260;
				return {
					i,
					dx: Math.cos(angle) * power * (side === 'L' ? 1 : -1),
					dy: Math.sin(angle) * power - Math.random() * 140,
					rotate: Math.random() * 720 - 360,
					delay: Math.random() * 0.18,
					duration: 1.4 + Math.random() * 1.0,
					color: CONFETTI_PALETTE[i % CONFETTI_PALETTE.length],
					shape: CONFETTI_SHAPES[i % CONFETTI_SHAPES.length],
					size: 6 + Math.random() * 9
				};
			});
		return { fall: fallPieces, burstL: makeBurst('L'), burstR: makeBurst('R') };
	}, []);

	const shapeClass = (shape) => (shape === 'circle' ? 'rounded-full' : shape === 'strip' ? 'rounded-sm' : 'rounded-[2px]');
	const dims = (shape, size) =>
		shape === 'strip'
			? { w: size * 0.4, h: size * 1.6 }
			: shape === 'circle'
				? { w: size * 0.8, h: size * 0.8 }
				: { w: size * 0.9, h: size * 0.6 };

	return (
		<div className="pointer-events-none fixed inset-0 z-[80] overflow-hidden">
			<div
				className="absolute left-1/2 top-1/2 h-[140%] w-[140%] -translate-x-1/2 -translate-y-1/2 rounded-full"
				style={{
					background: 'radial-gradient(closest-side, hsl(var(--primary) / 0.22), transparent 70%)',
					animation: 'reveal-flash 1.2s ease-out forwards'
				}}
			/>
			{fall.map((p) => {
				const d = dims(p.shape, p.size);
				return (
					<span
						key={`f-${p.i}`}
						className={`absolute top-[-10%] block ${shapeClass(p.shape)}`}
						style={{
							left: `${p.left}%`,
							width: `${d.w}px`,
							height: `${d.h}px`,
							background: p.color,
							transform: `rotate(${p.rotate}deg)`,
							animation: `confetti-fall ${p.duration}s ${p.delay}s cubic-bezier(.2,.6,.4,1) forwards`,
							'--drift': `${p.drift}px`
						}}
					/>
				);
			})}
			{[
				{ key: 'bl', side: 'left-0', pieces: burstL },
				{ key: 'br', side: 'right-0', pieces: burstR }
			].map((cannon) =>
				cannon.pieces.map((p) => {
					const d = dims(p.shape, p.size);
					return (
						<span
							key={`${cannon.key}-${p.i}`}
							className={`absolute bottom-0 ${cannon.side} block ${shapeClass(p.shape)}`}
							style={{
								width: `${d.w}px`,
								height: `${d.h}px`,
								background: p.color,
								animation: `confetti-burst ${p.duration}s ${p.delay}s cubic-bezier(.15,.8,.3,1) forwards`,
								'--dx': `${p.dx}px`,
								'--dy': `${p.dy}px`,
								'--rot': `${p.rotate}deg`
							}}
						/>
					);
				})
			)}
			<style>{`
				@keyframes confetti-fall {
					0% { transform: translate3d(0,0,0) rotate(0deg); opacity: 1; }
					100% { transform: translate3d(var(--drift,0), 120vh, 0) rotate(900deg); opacity: 0.95; }
				}
				@keyframes confetti-burst {
					0% { transform: translate3d(0,0,0) rotate(0deg); opacity: 1; }
					70% { opacity: 1; }
					100% { transform: translate3d(var(--dx,0), calc(var(--dy,0) + 60vh), 0) rotate(var(--rot,360deg)); opacity: 0; }
				}
				@keyframes reveal-flash {
					0% { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
					40% { opacity: 1; }
					100% { transform: translate(-50%, -50%) scale(1); opacity: 0; }
				}
			`}</style>
		</div>
	);
}

const TILE_PALETTE = [
	'from-indigo-100 to-indigo-50',
	'from-orange-100 to-orange-50',
	'from-emerald-100 to-emerald-50',
	'from-pink-100 to-pink-50',
	'from-sky-100 to-sky-50',
	'from-amber-100 to-amber-50'
];

/** A miniature browser window showing the retailer's freshly-live storefront. */
function StorefrontPreview({ shopName, displayUrl, productCount, products }) {
	const tiles = products.length > 0 ? products : Array.from({ length: 6 }).map(() => ({}));
	return (
		<div className="overflow-hidden rounded-2xl border border-border/60 bg-white shadow-[0_18px_40px_-22px_rgba(15,23,42,0.25)]">
			<div className="flex items-center gap-2 border-b border-border/60 bg-muted px-3 py-2">
				<div className="flex items-center gap-1">
					<span className="h-2.5 w-2.5 rounded-full bg-destructive/70" />
					<span className="h-2.5 w-2.5 rounded-full bg-action/70" />
					<span className="h-2.5 w-2.5 rounded-full bg-success/70" />
				</div>
				<div className="mx-2 flex flex-1 items-center gap-1.5 truncate rounded-md border border-border/60 bg-white px-3 py-1 text-[11px] text-muted-foreground">
					<Globe2 className="h-3 w-3 shrink-0" />
					{displayUrl}
				</div>
			</div>
			<div className="p-4">
				<div className="mb-3 flex items-center justify-between">
					<div>
						<div className="text-sm font-bold text-foreground">{shopName}</div>
						<div className="text-[11px] text-muted-foreground">{productCount.toLocaleString('en-GB')} products in stock</div>
					</div>
					<span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-success-soft-foreground">
						<span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" /> Live
					</span>
				</div>
				<div className="grid grid-cols-3 gap-2">
					{tiles.slice(0, 6).map((item, i) => (
						<div key={i} className="overflow-hidden rounded-lg border border-border/60 bg-white">
							<div className={`h-14 bg-gradient-to-br ${TILE_PALETTE[i % TILE_PALETTE.length]}`} />
							<div className="p-1.5">
								<div className="truncate text-[10px] font-semibold text-foreground">{item.name ?? 'In-store product'}</div>
								{Number.isFinite(item.price) ? (
									<div className="text-[10px] text-muted-foreground">£{item.price.toFixed(2)}</div>
								) : (
									<div className="text-[10px] text-muted-foreground">In stock</div>
								)}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

/**
 * The "matching your products" celebratory scene — a rising bar paired with
 * a count-up of the shop's searchable (publicly-profiled) products. The bar
 * always rises to a fixed 85% so every shop gets the same celebratory
 * animation regardless of how complete their catalogue is; the headline
 * counter still ticks up to the real searchable figure. The scene is skipped
 * entirely when we don't have the searchable / in-store counts.
 */
function CounterScene({ total, searchable, remaining, onNext }) {
	// Bar fill is decoupled from the real searchable/total ratio so every shop
	// gets the same celebratory rise, regardless of how complete their catalogue
	// is. The real numbers are still shown in the counter at the top of the scene.
	const fillPct = 85;
	const [filled, setFilled] = useState(false);
	const [showNext, setShowNext] = useState(false);
	const count = useCountUp(searchable, BAR_FILL_MS, true);
	const done = count >= searchable;

	useEffect(() => {
		const raf = requestAnimationFrame(() => setFilled(true));
		return () => cancelAnimationFrame(raf);
	}, []);
	useEffect(() => {
		const t = setTimeout(() => setShowNext(true), BAR_FILL_MS + 200);
		return () => clearTimeout(t);
	}, []);

	return (
		<div className="relative bg-gradient-unify p-8 text-center text-white sm:p-10">
			<div className="mx-auto mb-4 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white ring-1 ring-white/30">
				Matching your in-store products
			</div>
			<div className="flex flex-wrap items-center justify-center gap-2">
				<span className="text-4xl font-extrabold tracking-tight tabular-nums sm:text-5xl">{count.toLocaleString('en-GB')}</span>
				<span className="text-lg font-bold sm:text-xl">searchable products!</span>
				{done ? (
					<span className="inline-flex h-6 w-6 animate-scale-in items-center justify-center rounded-full bg-white text-primary">
						<Check className="h-3.5 w-3.5" />
					</span>
				) : null}
			</div>
			<p className="mx-auto mt-2 max-w-md text-sm text-white/85">
				Nice! Your barcodes were automatically populated with publicly available product profiles.
				{remaining > 0 ? (
					<>
						{' '}
						To get the rest live, check, change and add the final{' '}
						<span className="font-semibold">{remaining.toLocaleString('en-GB')}</span> in "Inventory".
					</>
				) : null}
			</p>

			<div className="mt-7 flex justify-center">
				<div className="relative h-56 w-16 overflow-hidden rounded-full bg-white/15 ring-1 ring-white/25">
					<div
						className="absolute inset-x-0 bottom-0 rounded-full bg-white transition-[height] ease-out"
						style={{ height: filled ? `${fillPct}%` : '0%', transitionDuration: `${BAR_FILL_MS}ms` }}
					/>
					<div className="absolute -top-1 left-full ml-3 whitespace-nowrap text-[11px] font-semibold text-white/80">
						<div className="flex items-center gap-1.5">
							<span className="h-px w-3 bg-white/40" />
							{total.toLocaleString('en-GB')} in-store
						</div>
					</div>
				</div>
			</div>

			{showNext ? (
				<div className="mt-7 flex animate-fade-in justify-center">
					<button
						type="button"
						onClick={onNext}
						className="inline-flex items-center gap-1.5 rounded-full bg-white px-6 py-3 text-sm font-semibold text-foreground transition hover:bg-white/90"
					>
						Next <ArrowRight className="h-4 w-4" />
					</button>
				</div>
			) : null}
		</div>
	);
}

/**
 * Storefront launch celebration modal — four phases:
 *
 *   1. anticipation — a brand-gradient panel with a rolling status checklist
 *      and progress bar. `onActivated` fires once when it completes.
 *   2. counter — a rising bar counting up the shop's searchable products
 *      against its in-store total (skipped when those counts aren't known).
 *   3. reveal — confetti, "you're live", and a miniature preview of the
 *      retailer's now-public storefront.
 *   4. next — a few first-day actions (view live, share, manage).
 *
 * Driven from the Channels first-channel empty state and the Overview's
 * "unlock your Storefront" card.
 */
export function LaunchStorefrontModal({ open, onClose, onActivated }) {
	const { shop } = useShop();
	const { summary } = useInventoryPageSummary(shop?.id);
	const [phase, setPhase] = useState('anticipation');
	const [stepIdx, setStepIdx] = useState(0);
	const [copied, setCopied] = useState(false);
	const [products, setProducts] = useState([]);

	const shopName = shop?.name ?? 'Your store';
	const inStock = summary?.inStockItems ?? 0;
	// Counter-phase figures: how many in-store products are ready to be
	// searchable (in stock with no blocking data errors — the same gate channel
	// visibility uses, minus the channel flag) vs the total in stock. From the
	// real inventory summary; the counter is skipped when those aren't known.
	const searchable = summary?.searchableCount ?? null;
	const totalInStore = inStock || summary?.totalItems || 0;
	const remaining = searchable != null ? Math.max(0, totalInStore - searchable) : 0;
	const showCounter = searchable != null && searchable > 0 && totalInStore > 0;
	const showCounterRef = useRef(showCounter);
	showCounterRef.current = showCounter;
	const storefrontSlug = shop?.slug ?? slugify(shopName);
	const storefrontUrl = `https://near.st/shop/${storefrontSlug}`;
	const storefrontDisplayUrl = `near.st/shop/${storefrontSlug}`;

	// `onActivated` is usually an inline callback — keep it in a ref so the
	// anticipation timer isn't reset every render.
	const onActivatedRef = useRef(onActivated);
	useEffect(() => {
		onActivatedRef.current = onActivated;
	}, [onActivated]);

	useEffect(() => {
		if (!open) {
			setPhase('anticipation');
			setStepIdx(0);
			setCopied(false);
		}
	}, [open]);

	useEffect(() => {
		if (!open || phase !== 'anticipation') return undefined;
		if (stepIdx >= ANTICIPATION_STEPS.length) {
			const t = setTimeout(() => {
				setPhase(showCounterRef.current ? 'counter' : 'reveal');
				onActivatedRef.current?.();
			}, 200);
			return () => clearTimeout(t);
		}
		const t = setTimeout(() => setStepIdx((s) => s + 1), ANTICIPATION_STEPS[stepIdx].ms);
		return () => clearTimeout(t);
	}, [open, phase, stepIdx]);

	// Escape closes the modal — but never during anticipation, when the
	// celebration is mid-flight and there's no close affordance on screen.
	useEffect(() => {
		if (!open) return undefined;
		const onKeyDown = (event) => {
			if (event.key === 'Escape' && phase !== 'anticipation' && phase !== 'counter') onClose?.();
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [open, phase, onClose]);

	useEffect(() => {
		if (!open || !shop?.organisation || !shop?.id) return;
		let cancelled = false;
		(async () => {
			try {
				const collection = await getSearchCollection(shop.organisation, shop.id);
				const data = await collection.search({ q: '*', query_by: 'title', filter_by: 'quantity:>0', per_page: 6 }, {});
				if (cancelled) return;
				setProducts(
					(data.hits ?? []).map(({ document }) => ({
						name: document.title,
						price: Number.isFinite(Number(document.price)) ? Number(document.price) : undefined
					}))
				);
			} catch {
				/* preview falls back to placeholder tiles */
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [open, shop?.organisation, shop?.id]);

	if (!open) return null;

	const copyLink = async () => {
		if (await copyToClipboard(storefrontUrl)) {
			setCopied(true);
			setTimeout(() => setCopied(false), 1800);
		}
	};

	return (
		<div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto p-4 sm:items-center sm:p-6">
			<div
				className="fixed inset-0 bg-foreground/60 backdrop-blur-sm animate-fade-in"
				onClick={phase === 'reveal' || phase === 'next' ? onClose : undefined}
			/>

			{phase === 'reveal' && !prefersReducedMotion() ? <Confetti /> : null}

			<div
				role="dialog"
				aria-modal="true"
				aria-label="Launching your storefront"
				className="relative my-auto max-h-[calc(100vh-2rem)] w-full max-w-xl overflow-y-auto rounded-3xl bg-card shadow-2xl ring-1 ring-border/60 animate-scale-in sm:max-h-[calc(100vh-3rem)]"
			>
				{phase === 'reveal' || phase === 'next' ? (
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-muted-foreground shadow-sm transition hover:bg-white hover:text-foreground"
					>
						<X className="h-4 w-4" />
					</button>
				) : null}

				{phase === 'anticipation' ? (
					<div className="relative bg-gradient-unify p-10 text-center text-white">
						<div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/30">
							<Rocket className="h-8 w-8 animate-pulse text-white" />
						</div>
						<h2 className="text-2xl font-extrabold tracking-tight">Launching your Local Storefront</h2>
						<p className="mt-2 text-sm text-white/80">Hold tight — we're putting your shop in front of shoppers.</p>
						<div className="mx-auto mt-7 max-w-sm">
							<div className="h-1.5 w-full overflow-hidden rounded-full bg-white/20">
								<div
									className="h-full rounded-full bg-white transition-[width] duration-500 ease-out"
									style={{
										width: `${Math.min(100, (Math.min(stepIdx, ANTICIPATION_STEPS.length) / ANTICIPATION_STEPS.length) * 100)}%`
									}}
								/>
							</div>
							<div className="mt-2 text-right text-[11px] text-white/70">
								Step {Math.min(stepIdx + 1, ANTICIPATION_STEPS.length)} of {ANTICIPATION_STEPS.length}
							</div>
							<div className="relative mt-4 h-[108px] overflow-hidden text-left">
								{ANTICIPATION_STEPS.map((s, i) => {
									const offset = i - stepIdx;
									const visible = offset >= -1 && offset <= 1;
									const done = offset < 0;
									const active = offset === 0;
									return (
										<div
											key={s.label}
											aria-hidden={!visible}
											className="absolute left-0 right-0 flex items-center gap-3 text-sm transition-all duration-500 ease-out"
											style={{
												transform: `translateY(${(offset + 1) * 36}px)`,
												opacity: visible ? (active ? 1 : 0.7) : 0
											}}
										>
											<span
												className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${done ? 'bg-white text-primary' : 'bg-white/20 text-white'}`}
											>
												{done ? (
													<Check className="h-3 w-3" />
												) : active ? (
													<span className="h-2 w-2 animate-pulse rounded-full bg-white" />
												) : (
													<span className="h-1.5 w-1.5 rounded-full bg-white/60" />
												)}
											</span>
											<span className="truncate text-white/95">{s.label}</span>
										</div>
									);
								})}
							</div>
						</div>
					</div>
				) : null}

				{phase === 'counter' ? (
					<CounterScene total={totalInStore} searchable={searchable} remaining={remaining} onNext={() => setPhase('reveal')} />
				) : null}

				{phase === 'reveal' ? (
					<div className="relative p-6 text-center sm:p-7">
						<div className="mx-auto mb-4 inline-flex items-center gap-1.5 rounded-full bg-success-soft px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-success-soft-foreground">
							<span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" /> You're live
						</div>
						<h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
							Your <span className="text-gradient-unify">Local Storefront</span> is live
						</h2>
						<div className="mt-5 text-left">
							<StorefrontPreview
								shopName={shopName}
								displayUrl={storefrontDisplayUrl}
								productCount={inStock}
								products={products}
							/>
						</div>
						<p className="mx-auto mt-5 max-w-md text-base font-semibold text-foreground">
							Now check it out and <span className="text-gradient-unify">share it with local shoppers</span> to increase your
							sales!
						</p>
						<button
							type="button"
							onClick={() => setPhase('next')}
							className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background transition hover:bg-foreground/90"
						>
							Next <ArrowRight className="h-4 w-4" />
						</button>
					</div>
				) : null}

				{phase === 'next' ? (
					<div className="relative p-8">
						<div className="mb-6 text-center">
							<h2 className="text-2xl font-extrabold tracking-tight">A few ways to get even more out of it</h2>
							<p className="mt-1.5 text-sm text-muted-foreground">You're live. Here's what works best on day one.</p>
						</div>

						<a
							href={storefrontUrl}
							target="_blank"
							rel="noreferrer"
							className="mb-3 flex items-center gap-3 rounded-2xl border-0 bg-gradient-unify p-4 text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-12px_hsl(var(--primary)/0.45)]"
						>
							<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/20 text-white">
								<Store className="h-4 w-4" />
							</div>
							<div className="min-w-0 flex-1">
								<div className="text-sm font-bold text-white">View your live storefront</div>
								<div className="text-xs text-white/80">See what shoppers see — exactly as it goes out.</div>
							</div>
							<ExternalLink className="h-4 w-4 text-white/80" />
						</a>

						<div className="mb-3 rounded-2xl border border-border/60 bg-gradient-soft p-4">
							<div className="mb-2 flex items-center gap-3">
								<div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
									<Share2 className="h-4 w-4" />
								</div>
								<div className="min-w-0">
									<div className="text-sm font-bold text-foreground">Share your storefront</div>
									<div className="text-xs text-muted-foreground">Tell your customers and socials you're online.</div>
								</div>
							</div>
							<div className="flex items-center gap-2">
								<div className="flex-1 truncate rounded-lg border border-border/60 bg-white px-3 py-2 text-xs text-muted-foreground">
									{storefrontUrl}
								</div>
								<button
									type="button"
									onClick={copyLink}
									className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-xs font-semibold text-background transition hover:bg-foreground/90"
								>
									{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
									{copied ? 'Copied' : 'Copy link'}
								</button>
							</div>
						</div>

						<button
							type="button"
							onClick={onClose}
							className="mt-6 w-full text-center text-xs text-muted-foreground transition hover:text-foreground"
						>
							I'll come back to this later
						</button>
					</div>
				) : null}
			</div>
		</div>
	);
}
